// schema
import * as ISchema from "#webapp/src/__schema__";
import { GSUITE_ROOM_ID } from "@clockwise/client-commons/src/util/room";

export const WEIRD_VALID_GOOGLE_RESOURCE_CALENDAR_DOMAINS = ["c", "group.calendar.google.com"];

//////////////////
// UTIL FNs
//////////////////
type TCalIdToPerson = { [calId: string]: ISchema.IPerson };
type TCalIdToOrgPerson = { [calId: string]: ISchema.IOrgPerson };
type TMatchedPersons = { matched: ISchema.IPerson[]; unMatched: string[] };

// outputs an array of persons
export function calIdsToPersons(persons: ISchema.IPerson[], calIds: string[]): TMatchedPersons {
  if (!persons || !persons.length) {
    return { matched: [], unMatched: [] };
  }

  const calIdsSet = new Set(calIds);
  // note: order matters here, we want to use orgMember profiles over externalOrgMember profiles
  const matched = persons.filter((person) => {
    const intersection = person.emails.filter((email) => calIdsSet.has(email));
    const isAMatch = intersection.length >= 1;
    if (isAMatch) {
      // avoid returning additional profiles for these emails
      intersection.forEach((email) => calIdsSet.delete(email));
    }
    return intersection.length >= 1;
  });
  return { matched, unMatched: Array.from(calIdsSet) };
}

export function calIdPersonMapFromPersons(
  persons: ISchema.IPerson[],
  calIds: string[],
): TCalIdToPerson {
  const calIdsSet = new Set(calIds);
  const calIdsToPersonMap: TCalIdToPerson = {};
  persons.forEach((person) => {
    const intersection = [person.primaryEmail, ...person.emails].filter((email) =>
      calIdsSet.has(email),
    );
    if (intersection.length >= 1) {
      // retain first match over later ones — externalOrgMember profiles are more current than orgMembers
      calIdsToPersonMap[intersection[0]] = calIdsToPersonMap[intersection[0]] || person;
    }
  });
  return calIdsToPersonMap;
}

export function calIdOrgPersonMapFromOrgPersons(
  orgPersons: ISchema.IOrgPerson[],
  calIds: string[],
): TCalIdToOrgPerson {
  const calIdsSet = new Set(calIds);
  const calIdsToPersonMap: TCalIdToOrgPerson = {};
  orgPersons.forEach((person) => {
    const intersection = [person.primaryCalendarId, ...person.calendarAliases].filter((email) =>
      calIdsSet.has(email),
    );
    if (intersection.length >= 1) {
      // retain first match over later ones — externalOrgMember profiles are more current than orgMembers
      calIdsToPersonMap[intersection[0]] = calIdsToPersonMap[intersection[0]] || person;
    }
  });
  return calIdsToPersonMap;
}

export function calendarIdsToPersonMap(org: ISchema.IOrg) {
  const personMap: { [calendarId: string]: ISchema.IPerson } = {};
  if (org.personsByCalendarIdsErrorable.__typename === "PersonList") {
    const personList = org.personsByCalendarIdsErrorable.list;
    personList.forEach((person) => {
      personMap[person.primaryCalendar as string] = person;
    });
  }

  return personMap;
}

export function calendarIdsToPersons(calendarIds: string[], org: ISchema.IOrg) {
  const personMap = calendarIdsToPersonMap(org);
  return calendarIds.map((calId) => {
    if (personMap[calId]) {
      return personMap[calId];
    }

    return { primaryEmail: calId, primaryCalendar: calId };
  });
}

export function calendarIdsToPersonAndCalendarIdsMap(calendarIds: string[], org: ISchema.IOrg) {
  const personMap = calendarIdsToPersonMap(org);
  const persons: ISchema.IPerson[] = [];
  const nonPersonCalendarIds: string[] = [];

  calendarIds.map((calId) => {
    if (personMap[calId]) {
      return persons.push(personMap[calId]);
    }
    return nonPersonCalendarIds.push(calId);
  });

  return {
    persons,
    nonPersonCalendarIds,
  };
}

export function getDomainFromCalendarId(calendarId: string) {
  if (
    calendarId.split("@")[1] === GSUITE_ROOM_ID ||
    calendarId.split("@")[1] === "group.calendar.google.com" ||
    calendarId.endsWith("@g") // weird way to handle cut off decoded google ids that happen with group ids..
  ) {
    return calendarId.split("_")[0];
  } else {
    return calendarId.split("@")[1];
  }
}

export function isShadowCalendarId(calendarId: string, shadowCalendarId: string | null) {
  const isShadowCalendar =
    calendarId === shadowCalendarId ||
    (!!shadowCalendarId && shadowCalendarId.startsWith(calendarId) && calendarId.endsWith("@g"));
  return isShadowCalendar;
}
